import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { Card, Inline, Text, Notice, textVariants, Stack } from '@etg/wings';
import { constants } from '@eti/utils';
import { useRef } from 'react';
import { isCarrierCodesPresentInAllSegments } from '../../../common/utils/carriersUtils';
import PersuasionModalProvider from '../../../common/context/PersuasionModalContext';
import getTranslatedTexts from '../../../common/text-resolvers/utils/getTranslatedTexts';
import { generateReadMore } from '../../../common/utils/readMore';
import { CABIN_BAGGAGE } from '../../../products';
import { bundleContentDetail, cabinBagDetails, cabinBaggageDescription, getTravelersPrimaryFlow } from '../utils/cabinBaggageUtils';
import BaggageSectionHeader from '../../common/components/BaggageSectionHeader';
import BaggageSectionDescription from '../../common/components/BaggageSectionDescription';
import BaggageIllustration from '../../common/components/BaggageIllustration';
import BaggageStatus from '../../common/components/BaggageStatus';
import ApplySelectionToAllCheckbox from '../../common/components/ApplySelectionToAllCheckbox';
import BaggageSelection from '../../common/components/BaggageSelection';
import useBaggageVisibilityTracker from '../../common/hooks/useBaggageVisibilityTracker';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const generalTexts = {
  descriptionLabel: 'Product.CabinBaggagePerTraveler.Description.Label',
  includedBaggageStatusLabel: 'TravelerDetails.Status.Included',
  notAvailableCabinBaggageText: 'Product.CabbinBaggagePerTraveler.Not.Available.To.Offer.Text',
  notAvailableCabinBaggageBadgeStatusText: 'TravelerDetails.Status.Unavailable',
  offeringText: 'Product.CabinBaggage.Integrated.Offering.Text',
  pricePerBoundLabel: 'Products.Price.Label.PerBounds',
  persuasionContent: 'cabinBaggagePerTravelerPersuasionContent',
  readMoreContent: 'cabinBaggagePerTravelerReadMoreModalContent',
  sameChoiceForAllLabel: 'Product.CabinBaggage.SelectSameChoiceForAll',
  title: 'cabinBaggagePerTravelerTitle',
  titleChoiceNo: 'cabinBaggagePerTravelerConfirmationNo'
};
const productName = CABIN_BAGGAGE.name;
const cabinBaggageCardStyles = css`
  border: 1px solid ${etiColors.grayScale[4]};
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-block-end: 16px;
`;
const baggageSelectionStyles = css`
  margin-top: 12px;
  width: 85%;

  @media ${mediaQueries.small.only} {
    margin-top: 0;
    width: 80%;
  }
`;
const notAvailableBaggageStatusTextStyle = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const noticeTextStyles = css`
  max-width: 85%;
`;
const CabinBaggagePrimaryFlow = _ref => {
  var _traveler$markup;
  let {
    bundleData,
    firstTravelerSelectionValue,
    totalBaggageQuantity,
    includedBaggage,
    isBaggageAvailableOrIncluded,
    order,
    productPosition,
    readMore = {
      isEnabled: true
    },
    shouldApplySelectionToAll,
    onChangeAll,
    onChange,
    shouldUseCarrierSpecificUnavailableLabel,
    travelerNumber
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    p
  } = useProperty();
  const {
    isRTL
  } = useDirection();
  const rootElement = useRef(null);
  const isCabinBaggageBundle = bundleContentDetail(bundleData);
  const includedCabinBaggageInTrip = includedBaggage || isCabinBaggageBundle;
  const isCabinBaggageIncludedInTrip = includedCabinBaggageInTrip && Object.keys(includedCabinBaggageInTrip).length !== 0;
  const availableForPurchase = isBaggageAvailableOrIncluded && !isCabinBaggageIncludedInTrip;
  const notAvailableForPurchase = !isBaggageAvailableOrIncluded && !isCabinBaggageIncludedInTrip;
  useBaggageVisibilityTracker({
    isBaggageAvailableOrIncluded,
    includedBaggageInTrip: isCabinBaggageIncludedInTrip,
    productName,
    rootElement,
    productPosition
  });
  const {
    descriptionLabel,
    includedBaggageStatusLabel,
    notAvailableCabinBaggageBadgeStatusText,
    notAvailableCabinBaggageText,
    offeringText,
    persuasionContent,
    readMoreContent,
    sameChoiceForAllLabel,
    title,
    titleChoiceNo
  } = getTranslatedTexts(generalTexts, t);
  const ryanairCarrierCodes = p('IbeClient.RyanairMarketingCarriers') || [];
  const isRyanAirCarrierPresent = isCarrierCodesPresentInAllSegments(ryanairCarrierCodes, order.bounds);
  const {
    pieces,
    dimensions,
    weight,
    weightUnit
  } = cabinBagDetails({
    bundleData,
    sellSpecification: order.sellSpecification,
    includedBaggage
  });
  const cabinBaggagePiecesWeightSizeDescription = cabinBaggageDescription({
    pieces,
    weight,
    weightUnit,
    dimensions,
    t
  });
  const travelers = getTravelersPrimaryFlow(order);
  const traveler = travelers[travelerNumber] || {};
  const isInfant = traveler.ageType === constants.ageTypes.INFANT;
  const isFirstTraveler = Number(traveler.id) === 0;
  const baggageOptions = [{
    id: `content-false-${travelerNumber}`,
    label: titleChoiceNo !== null && titleChoiceNo !== void 0 ? titleChoiceNo : null,
    priceRaw: 0,
    markup: 0,
    value: 'false'
  }, {
    id: `content-true-${travelerNumber}`,
    label: cabinBaggagePiecesWeightSizeDescription,
    priceRaw: traveler === null || traveler === void 0 ? void 0 : traveler.price,
    markup: (_traveler$markup = traveler === null || traveler === void 0 ? void 0 : traveler.markup) !== null && _traveler$markup !== void 0 ? _traveler$markup : 0,
    value: 'true'
  }];
  return /*#__PURE__*/_jsx(_Fragment, {
    children: !isInfant && /*#__PURE__*/_jsx("div", {
      ref: rootElement,
      children: /*#__PURE__*/_jsxs(Card, {
        className: cabinBaggageCardStyles,
        "data-testid": `travelerDetails-${productName}-card-${traveler.id}`,
        children: [/*#__PURE__*/_jsx(BaggageSectionHeader, {
          productName: productName,
          readMoreModal: generateReadMore(readMore, readMoreContent),
          title: title,
          travelerId: traveler.id
        }), /*#__PURE__*/_jsx(BaggageSectionDescription, {
          description: descriptionLabel,
          productName: productName,
          travelerId: traveler.id
        }), /*#__PURE__*/_jsxs(Inline, {
          "data-testid": `travelerDetails-productSection-product-${productName}-body-${traveler.id}`,
          children: [/*#__PURE__*/_jsx(BaggageIllustration, {
            isRTL: isRTL,
            productName: productName
          }), notAvailableForPurchase && /*#__PURE__*/_jsx(BaggageStatus, {
            className: notAvailableBaggageStatusTextStyle,
            dataTestId: "notAvailableBaggage-status",
            description: shouldUseCarrierSpecificUnavailableLabel ? notAvailableCabinBaggageText : '',
            statusLabel: notAvailableCabinBaggageBadgeStatusText,
            useNotice: true,
            variant: "caution"
          }), isCabinBaggageIncludedInTrip && /*#__PURE__*/_jsx(BaggageStatus, {
            dataOfferRate: CABIN_BAGGAGE.id,
            dataTestId: "includedBaggage-status",
            description: cabinBaggagePiecesWeightSizeDescription,
            statusLabel: includedBaggageStatusLabel,
            variant: "positive"
          }), availableForPurchase ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(PersuasionModalProvider, {
              children: /*#__PURE__*/_jsxs(Stack, {
                className: baggageSelectionStyles,
                spacing: 8,
                children: [/*#__PURE__*/_jsx(BaggageSelection, {
                  baggageOptions: baggageOptions,
                  onChange: onChange,
                  order: order,
                  persuasionContent: persuasionContent,
                  persuasionTitle: title,
                  productId: CABIN_BAGGAGE.id,
                  productName: productName,
                  productPosition: productPosition,
                  radioGroupScreenReaderDescription: descriptionLabel,
                  rootElement: rootElement,
                  shouldApplySelectionToAll: shouldApplySelectionToAll,
                  totalBaggageQuantity: totalBaggageQuantity,
                  travelerId: traveler.id
                }), isRyanAirCarrierPresent && offeringText && /*#__PURE__*/_jsx(Notice, {
                  "data-testid": `ryanair-offering-text-${traveler.id}`,
                  variant: "promotional",
                  children: /*#__PURE__*/_jsx(Text, {
                    asHtml: true,
                    className: noticeTextStyles,
                    variant: textVariants._14_BOLD,
                    children: offeringText
                  })
                })]
              })
            }), isFirstTraveler && travelers.length > 1 && /*#__PURE__*/_jsx(ApplySelectionToAllCheckbox, {
              firstTravelerSelectionValue: firstTravelerSelectionValue,
              label: sameChoiceForAllLabel,
              onChangeAll: onChangeAll,
              productName: productName,
              shouldApplySelectionToAll: shouldApplySelectionToAll
            })]
          }) : null]
        })]
      })
    })
  });
};
export default CabinBaggagePrimaryFlow;